import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import {
  Observable,
  of as observableOf,
  throwError as observableThrowError,
} from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { CurrentUserService } from '®shared/current-user';
import { Permissions, hasPermissionForListId } from '®shared/permissions';
import {
  DEFAULT_NAVIGATION_EXTRAS,
  handleRenavigationError,
  idWithoutDotHtml,
} from '®shared/router';

import { ListResolverStoreService } from '../resolver-store';

@Injectable()
export class CanActivateListView implements CanActivate {
  constructor(
    private currentUserService: CurrentUserService,
    private listResolverStoreService: ListResolverStoreService,
    private router: Router,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const listId = idWithoutDotHtml(route.params.listId);
    const { version, tenantCode } = route.params;

    return this.currentUserService.getCurrentUser().pipe(
      mergeMap(currentUser => {
        const { permissions, oauthUser } = currentUser;
        const { isAnonymous } = oauthUser;

        // If you have permission to edit the list, and have a TARL profile redirect to list edit
        if (
          hasPermissionForListId(Permissions.EDIT_LIST, listId, permissions)
        ) {
          if (!currentUser.hasTarlUserProfile) {
            const redirectUrl = this.router.url;

            this.router.navigate(
              [version, tenantCode, 'my_profile', 'edit.html'],
              {
                preserveFragment: true,
                queryParamsHandling: 'merge',
                replaceUrl: true,
                skipLocationChange: false,
                queryParams: { redirectUrl },
              },
            );
          } else {
            this.router.navigate(
              [version, tenantCode, 'lists', listId, 'modes', 'edit'],
              DEFAULT_NAVIGATION_EXTRAS,
            );
          }
          return observableOf(false);
        }

        // If we find a published list, return true to allow access
        // If no published list is found BUT the user is non anonymous, redirect to draft;
        // otherwise, redirect to a 404 error page
        return this.listResolverStoreService
          .hasPublishedReadingList(listId)
          .pipe(
            catchError(error => {
              if (!(error instanceof HttpErrorResponse)) {
                // unknown errors, rethrown
                return observableThrowError(error);
              }

              if (error.status === 404 && !isAnonymous) {
                // a 404 means there's no published list, so check for a draft
                this.router.navigate(
                  [version, tenantCode, 'lists', listId, 'modes', 'view_draft'],
                  DEFAULT_NAVIGATION_EXTRAS,
                );
                return observableOf(false);
              } else if (error.status === 403 && isAnonymous) {
                // a 403 means the list is private, so force login
                this.currentUserService.login();
                return observableOf(false);
              }

              // all other errors are re-thrown
              return observableThrowError(error);
            }),
          );
      }),
      catchError(handleRenavigationError(this.router)),
    );
  }
}
